import fetchIntercept from "fetch-intercept";
import { routes, history } from "../router";
import Utils from "./Utils";
import IMASLog from "./IMASLog";
import PageContext from "./PageContext";
import { redirectToLogin } from "./Auth0Authenticator";
import { authRetryKey, beginAuthRetry, endAuthRetry } from "./authRetryGuard";

interface StoredRequestData {
  url: string;
  method: string;
  body?: string;
  contentType?: string;
}

const _pendingRequests = new Map<string, StoredRequestData[]>();
let _unregister: (() => void) | null = null;

function storeRequestData(data: StoredRequestData): void {
  const key = `${data.method}:${data.url}`;
  const queue = _pendingRequests.get(key) || [];
  _pendingRequests.set(key, [...queue, data]);
}

function popRequestData(url: string, method: string): StoredRequestData | undefined {
  const key = `${method}:${url}`;
  const queue = _pendingRequests.get(key);
  if (!queue || !queue.length) return undefined;
  const [first, ...rest] = queue;
  rest.length ? _pendingRequests.set(key, rest) : _pendingRequests.delete(key);
  return first;
}
import { IframeMessageType } from "../hooks/useIframe/useIframe.constants";
import { getParentOrigin } from "./parentTokenRelay";

export const registerFetchInterceptor = () => {
  if (_unregister) return;
  _unregister = fetchIntercept.register({
    request: async function (url: any, config) {
      // internal references
      let internal_apis = [
        "integritymarketing",
        "integrity",
        "azurewebsites.net",
        "localhost",
        "track",
        "httpapi",
      ];
      let isInternalApi = false;
      let urlToLoweerCase = url && JSON.stringify(url).toLowerCase();

      internal_apis.forEach((a) => {
        if (urlToLoweerCase.includes(a)) {
          isInternalApi = true;
        }
      });

      // The Auth0 tenant domain (e.g. report-auth-dev.integrity.com) matches the
      // "integrity" entry above. Treating Auth0's own requests as internal would make
      // this interceptor await Utils.getApiKey() -> the same token-refresh lock that
      // the in-flight getAccessTokenSilently() call (which triggered this very fetch)
      // is already holding, deadlocking until auth0-spa-js's own fetch timeout fires.
      const auth0Domain = Utils.getAuth0ClientIdByEnvironment('domain');
      if (auth0Domain && urlToLoweerCase.includes(auth0Domain.toLowerCase())) {
        isInternalApi = false;
      }

      // The anonymous /api/features/{flagKey} route (Integrity.Web.Gateway's Flipt
      // proxy, e.g. used by index.html's pre-auth bootstrap check) needs no auth and
      // is designed to resolve fast -- callers give it a short client-side timeout.
      // Treating it as an internal API would
      // gate every flag check behind an Auth0 token fetch, which can easily outlast
      // that timeout and abort the request before it's even sent, and would flip on
      // the global loading spinner for what's meant to be a cheap, silent check.
      if (urlToLoweerCase.includes("/api/features/")) {
        isInternalApi = false;
      }

      let withDefaults = Object.assign({}, config);
      if (isInternalApi) {
        let host = Utils.getHost();
        let authKey: string;
        try {
          authKey = await Utils.getApiKey();
        } catch {
          // Auth0 session expired before the request was sent — send to re-login
          localStorage.removeItem('auth0UserItem');
          redirectToLogin();
          return Promise.reject(new Error('Session expired'));
        }
        // Start with the original headers as a baseline:
        const modifiedHeaders = new Headers(withDefaults.headers);
        modifiedHeaders.set("HOSTNAME", host);
        modifiedHeaders.set("authorization", authKey);
        modifiedHeaders.set("Accept_Timezone", "" + Utils.getTimezoneOffset());
        if (PageContext.impersonatingUserId) {
          modifiedHeaders.set(
            "impersonatingUserId",
            PageContext.impersonatingUserId,
          );
        }
        let invitecode = localStorage.getItem("invite_code") || "";
        if (invitecode != "") {
          modifiedHeaders.set("Code_invite", invitecode);
        }
        withDefaults.headers = modifiedHeaders;
      }

      if (isInternalApi) {
        const originalHeaders = config && config.headers;
        const contentType: string | undefined =
          originalHeaders instanceof Headers
            ? (originalHeaders.get('content-type') || undefined)
            : originalHeaders
              ? ((originalHeaders as Record<string, string>)['content-type'] ||
                 (originalHeaders as Record<string, string>)['Content-Type'])
              : undefined;

        storeRequestData({
          url,
          method: ((config && config.method) || 'GET').toUpperCase(),
          body: config && typeof config.body === 'string' ? config.body : undefined,
          contentType,
        });
      }

      if (isInternalApi && !urlToLoweerCase.includes("chunk.js")) {
        PageContext.setIsMessageVisible(false);
      }
      PageContext.setIsLoadingVisible(true);
      return [url, withDefaults];
    },
    requestError: function (error) {
      IMASLog.log("request error");
      PageContext.setIsLoadingVisible(false);
      PageContext.setResponseMessage(error.response);
      PageContext.setIsMessageVisible(true);

      return Promise.reject(error);
    },
    response: async function (response) {
      PageContext.setIsLoadingVisible(false);
      PageContext.setUserAccess(true);

      if (response.status === 401) {
        PageContext.setUserAccess(false);
        const method = ((response.request && response.request.method) || 'GET').toUpperCase();
        const reqData = popRequestData(response.url, method);

        if (reqData && localStorage.getItem('auth0UserItem') != null) {
          const retryKey = authRetryKey(method, response.url);
          if (!beginAuthRetry(retryKey)) {
            // This is the response to our own retry. Hand the 401 back to the outer call instead of retrying again.
            return response;
          }
          try {
            const freshToken = await Utils.getApiKey();
            if (freshToken) {
              const retryInit: RequestInit = {
                method: reqData.method,
                body: reqData.body,
                headers: reqData.contentType
                  ? { 'Content-Type': reqData.contentType }
                  : undefined,
              };
              const retryResponse = await fetch(reqData.url, retryInit);
              if (retryResponse.status !== 401) {
                return retryResponse;
              }
            }
          } catch {
            // Auth0 session expired — send to re-login, not noAccess
            localStorage.removeItem('auth0UserItem');
            redirectToLogin();
            return response;
          } finally {
            endAuthRetry(retryKey);
          }
        }

        if (localStorage.getItem("auth0UserItem") != null) {
          response.clone().text().then((_responseText) => {
            routes.noAccess.replace();
            let errorTitle = "Unauthorized to access.";
            if (_responseText) {
              let obj = JSON.parse(_responseText);
              errorTitle = obj.title;
            }

            let error = {
              status: 401,
              title: errorTitle,
              errors: { "": [] },
            };
            PageContext.setResponseMessage(JSON.stringify(error));
            PageContext.setIsMessageVisible(true);
          });
          window.parent.postMessage(
            {
              type: IframeMessageType.LOGOUT,
              message: history.getActiveInstance().location.pathname,
            },
            getParentOrigin(),
          );
        }
        return response;
      }
      if (response.status === 404) {
        response.text().then((_responseText) => {
          let errorTitle = "Data not found.";
          if (_responseText) {
            let obj = JSON.parse(_responseText);
            errorTitle = obj.title;
          }

          let error = {
            status: 404,
            title: errorTitle,
            errors: { "": [] },
          };
          PageContext.setResponseMessage(JSON.stringify(error));
          PageContext.setIsMessageVisible(true);
        });
      }
      response.status === 400 && handle400ValidationErrors(response);
      return response;
    },
    responseError: function (error) {
      console.log(error);
      IMASLog.log("response error");
      PageContext.setIsLoadingVisible(false);
      PageContext.setResponseMessage(
        JSON.stringify(PageContext.getTimeOutResponse()),
      );
      PageContext.setIsMessageVisible(true);
      return Promise.reject(error);
    },
  });

  function handle400ValidationErrors(
    response: fetchIntercept.FetchInterceptorResponse,
  ) {
    let location = window.location.href.toLowerCase();
    var n = response.request.url.toLowerCase().lastIndexOf("/");
    var result = response.request.url.toLowerCase().substring(n + 1);
    if (
      response.request.url.includes("BulkUpdateContracts") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      response.request.url.toLowerCase().includes("documents/createdocument") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      response.request.url.toLowerCase().includes("documents/updatedocument") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("workflow") &&
      response.request.url.toLowerCase().includes("/documents") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      response.request.url.toLowerCase().includes("passwordlist") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("reports/customreports/add") &&
      result == "report" &&
      response.status == 400
    ) {
      throw response;
    } else if (
      location.includes("administration/users/edit") &&
      result == "users" &&
      response.status == 400
    ) {
      throw response;
    } else if (
      location.includes("administration/general/productlines/list") &&
      result == "productlines" &&
      response.status == 400
    ) {
      throw response;
    } else if (
      response.request.url.toLowerCase().includes("agents") &&
      (result == "createalert" || result == "updatealert") &&
      response.status == 400
    ) {
      throw response;
    } else if (
      location.includes("agents") &&
      response.request.url.toLowerCase().includes("contacts") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("agents") &&
      response.request.url.toLowerCase().includes("appointments") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      (location.includes("agents") || location.includes("supplies/add")) &&
      response.request.url.toLowerCase().includes("address") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("addcustomer") &&
      response.request.url.toLowerCase().includes("validatephonenumber") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("customers") &&
      response.request.url.toLowerCase().includes("insuredcontact") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("announcement/list") &&
      response.request.url.toLowerCase().includes("announcements") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("documents/packet") &&
      response.request.url.toLowerCase().includes("duplicatepacket") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("general/notifications") &&
      result === "notification" &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("agentgroups/list") &&
      response.request.url.toLowerCase().includes("agentgroups") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("customers") &&
      response.request.url.toLowerCase().includes("/policies/notes") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("licensestatusreasons") &&
      response.request.url.toLowerCase().includes("/licensestatusreason") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("payoutlevels") &&
      response.request.url.toLowerCase().includes("/payoutlevel") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("managerts") &&
      response.request.url
        .toLowerCase()
        .includes("/managerts/addrtscertification") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("contracts") &&
      response.request.url.toLowerCase().includes("/paymentplans/") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("docworkflow") &&
      (response.request.url.toLowerCase().includes("/replace") ||
        response.request.url.toLowerCase().includes("/notes") ||
        response.request.url.toLowerCase().includes("/documents") ||
        response.request.url.toLowerCase().includes("/attach") ||
        response.request.url.toLowerCase().includes("/sendto")) &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("submissionmanagement/sendersubmissiontypes") &&
      response.request.url
        .toLowerCase()
        .includes("/submissionsenderemailtypes") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("managerts") &&
      (response.status === 400 || response.status === 404)
    ) {
      throw response;
    } else if (
      location.includes("roles/list") &&
      (response.request.url.toLowerCase().includes("roles") ||
        response.request.url.toLowerCase().includes("cloneRole")) &&
      response.status === 400
    ) {
      throw response;
    } else if (
      (location.includes("agents") || location.includes("policies/edit")) &&
      response.request.url.toLowerCase().includes("policies/notes") &&
      response.status === 400
    ) {
      throw response;
    } else if (
      location.includes("carriermanagement") &&
      (response.request.url.toLowerCase().includes("addcarrier") ||
        response.request.url.toLowerCase().includes("updatecarrier")) &&
      response.status === 400
    ) {
      throw response;
    } else if (response.request.url.includes("marketereventlist")) {
      throw response;
    } else if (response.status === 500 || response.status === 400) {
      response.text().then((_responseText) => {
        let parsedResponse = JSON.parse(_responseText);
        let _title = parsedResponse.title;
        PageContext.setResponseMessage(_responseText);

        if (_title && !_title.includes("Contact with Phone Number")) {
          PageContext.setIsMessageVisible(true);
        }
      });
    }
  }
};
